import { useState } from 'react';
import { saveServerRespawn, type RespawnDefault } from '../api/servers';
import { ca } from '../i18n/ca';

const NONE: RespawnDefault = { mode: 'none', delay: 10, lives: 3, shared: false, waveMinutes: 5 };
const MODES: RespawnDefault['mode'][] = ['none', 'base', 'leader', 'lives', 'waves'];

/**
 * A server's respawn default, on its own page (the owner, 2026-10-30): what a
 * mission that says «per defecte del servidor» gets when it is played here.
 * Every server starts at "nobody comes back"; a mission that chose its own
 * respawn never reads this.
 */
export function ServerRespawnCard({
  serverId,
  current,
  canManage,
  onSaved,
}: {
  serverId: number;
  current: RespawnDefault | undefined;
  canManage: boolean;
  onSaved: (saved: RespawnDefault) => void;
}) {
  const t = ca.serversPage.respawn;
  const m = ca.design.respawnModel;
  const stored = current ?? NONE;
  const [value, setValue] = useState<RespawnDefault>(stored);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed =
    value.mode !== stored.mode ||
    value.delay !== stored.delay ||
    value.lives !== stored.lives ||
    value.shared !== stored.shared ||
    value.waveMinutes !== stored.waveMinutes;
  const patch = (p: Partial<RespawnDefault>) => setValue((v) => ({ ...v, ...p }));
  const number = (n: string, fallback: number) =>
    Number.isFinite(Number(n)) ? Number(n) : fallback;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      onSaved(await saveServerRespawn(serverId, value));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="card form" aria-label={t.title}>
      <h2>{t.title}</h2>
      <p className="muted small">{t.intro}</p>
      <fieldset disabled={!canManage || busy}>
        <label>
          {m.mode}
          <select
            name="respawnMode"
            value={value.mode}
            onChange={(e) => patch({ mode: e.target.value as RespawnDefault['mode'] })}
          >
            {MODES.map((mode) => (
              <option key={mode} value={mode}>
                {m.modes[mode]}
              </option>
            ))}
          </select>
        </label>
        <p className="muted small">{m.modeHints[value.mode]}</p>
        {(value.mode === 'base' || value.mode === 'leader' || value.mode === 'lives') && (
          <label>
            {m.delay}
            <input
              type="number"
              name="respawnDelay"
              min={0}
              max={900}
              value={value.delay}
              onChange={(e) => patch({ delay: number(e.target.value, value.delay) })}
            />
          </label>
        )}
        {value.mode === 'lives' && (
          <>
            <label>
              {m.lives}
              <input
                type="number"
                name="respawnLives"
                min={1}
                max={99}
                value={value.lives}
                onChange={(e) => patch({ lives: number(e.target.value, value.lives) })}
              />
            </label>
            <label className="check">
              <input
                type="checkbox"
                name="respawnShared"
                checked={value.shared}
                onChange={(e) => patch({ shared: e.target.checked })}
              />
              <span>{m.shared}</span>
            </label>
          </>
        )}
        {value.mode === 'waves' && (
          <label>
            {m.waveMinutes}
            <input
              type="number"
              name="respawnWaveMinutes"
              min={1}
              max={30}
              value={value.waveMinutes}
              onChange={(e) => patch({ waveMinutes: number(e.target.value, value.waveMinutes) })}
            />
          </label>
        )}
      </fieldset>
      {canManage && changed && (
        <div className="row actions">
          <button type="button" className="primary" disabled={busy} onClick={() => void save()}>
            {t.save}
          </button>
        </div>
      )}
      {error && (
        <p className="error small" role="alert">
          {error}
        </p>
      )}
    </article>
  );
}
